// ─────────────────────────────────────────────────────────────────────────────
// Spanish conjugation by RULE — no AI, no network. Covers what the grammar
// topics drill: regular present, regular preterite (with the -car/-gar/-zar
// spelling changes), and present-tense stem changers. Irregular verbs are
// listed so the teacher form can refuse them for a "regular" topic rather than
// quietly teaching a wrong form like "tenimos".
// ─────────────────────────────────────────────────────────────────────────────

export type Person = 'yo' | 'tú' | 'él' | 'nosotros' | 'vosotros' | 'ellos';
export const PEOPLE: Person[] = ['yo', 'tú', 'él', 'nosotros', 'vosotros', 'ellos'];

/** How each person is shown in a question. */
export const PERSON_LABEL: Record<Person, string[]> = {
  yo: ['yo'],
  tú: ['tú'],
  él: ['él', 'ella', 'usted'],
  nosotros: ['nosotros', 'nosotras'],
  vosotros: ['vosotros', 'vosotras'],
  ellos: ['ellos', 'ellas', 'ustedes'],
};

const PRESENT: Record<'ar' | 'er' | 'ir', string[]> = {
  ar: ['o', 'as', 'a', 'amos', 'áis', 'an'],
  er: ['o', 'es', 'e', 'emos', 'éis', 'en'],
  ir: ['o', 'es', 'e', 'imos', 'ís', 'en'],
};
const PRETERITE: Record<'ar' | 'er' | 'ir', string[]> = {
  ar: ['é', 'aste', 'ó', 'amos', 'asteis', 'aron'],
  er: ['í', 'iste', 'ió', 'imos', 'isteis', 'ieron'],
  ir: ['í', 'iste', 'ió', 'imos', 'isteis', 'ieron'],
};

/** Verbs that are NOT regular in the present and/or preterite. */
export const IRREGULAR = new Set([
  'ser', 'estar', 'ir', 'tener', 'venir', 'hacer', 'poner', 'salir', 'decir', 'oír', 'traer', 'caer',
  'dar', 'ver', 'saber', 'conocer', 'poder', 'querer', 'haber', 'caber', 'valer', 'andar', 'conducir',
  'traducir', 'producir', 'leer', 'creer', 'construir', 'destruir', 'incluir', 'huir', 'oler', 'reír',
  'dormir', 'morir', 'pedir', 'servir', 'seguir', 'sentir', 'preferir', 'mentir', 'repetir', 'vestir',
  'jugar', 'pensar', 'empezar', 'cerrar', 'despertar', 'entender', 'perder', 'querer', 'volver', 'contar',
  'costar', 'encontrar', 'mostrar', 'recordar', 'almorzar', 'probar', 'soñar', 'volar', 'llover', 'mover',
  'devolver', 'resolver', 'comenzar', 'sentar', 'nevar', 'regar', 'negar', 'defender', 'encender',
]);

/** Present-tense stem changers: the boot verbs. */
export const STEM_CHANGERS: Record<string, 'e>ie' | 'o>ue' | 'e>i' | 'u>ue'> = {
  pensar: 'e>ie', querer: 'e>ie', empezar: 'e>ie', cerrar: 'e>ie', entender: 'e>ie', perder: 'e>ie',
  preferir: 'e>ie', sentir: 'e>ie', comenzar: 'e>ie', despertar: 'e>ie', mentir: 'e>ie', nevar: 'e>ie',
  encender: 'e>ie', defender: 'e>ie', sentar: 'e>ie',
  poder: 'o>ue', volver: 'o>ue', dormir: 'o>ue', contar: 'o>ue', costar: 'o>ue', encontrar: 'o>ue',
  mostrar: 'o>ue', recordar: 'o>ue', almorzar: 'o>ue', probar: 'o>ue', soñar: 'o>ue', volar: 'o>ue',
  llover: 'o>ue', mover: 'o>ue', devolver: 'o>ue', morir: 'o>ue',
  pedir: 'e>i', servir: 'e>i', repetir: 'e>i', seguir: 'e>i', vestir: 'e>i', medir: 'e>i',
  jugar: 'u>ue',
};

export const DEFAULT_REGULAR = [
  'hablar', 'trabajar', 'estudiar', 'caminar', 'cocinar', 'bailar', 'cantar', 'escuchar', 'mirar', 'comprar',
  'comer', 'beber', 'aprender', 'correr', 'vender', 'comprender',
  'vivir', 'escribir', 'abrir', 'recibir', 'decidir', 'compartir',
];
export const DEFAULT_STEM = [
  'pensar', 'querer', 'empezar', 'cerrar', 'entender', 'preferir',
  'poder', 'volver', 'dormir', 'contar', 'encontrar', 'almorzar',
  'pedir', 'servir', 'repetir', 'jugar',
];
/** Regular in the preterite (only the yo spelling change, if any). */
export const DEFAULT_PRETERITE = [
  'hablar', 'trabajar', 'estudiar', 'caminar', 'cocinar', 'bailar', 'escuchar', 'comprar',
  'buscar', 'tocar', 'llegar', 'pagar', 'jugar', 'empezar', 'almorzar',
  'comer', 'beber', 'aprender', 'correr', 'vivir', 'escribir', 'abrir', 'salir', 'volver',
];

export const ENGLISH: Record<string, string> = {
  hablar: 'to speak', trabajar: 'to work', estudiar: 'to study', caminar: 'to walk', cocinar: 'to cook',
  bailar: 'to dance', cantar: 'to sing', escuchar: 'to listen', mirar: 'to watch', comprar: 'to buy',
  comer: 'to eat', beber: 'to drink', aprender: 'to learn', correr: 'to run', vender: 'to sell',
  comprender: 'to understand', vivir: 'to live', escribir: 'to write', abrir: 'to open', recibir: 'to receive',
  decidir: 'to decide', compartir: 'to share', pensar: 'to think', querer: 'to want', empezar: 'to start',
  cerrar: 'to close', entender: 'to understand', preferir: 'to prefer', poder: 'to be able to',
  volver: 'to return', dormir: 'to sleep', contar: 'to count / tell', encontrar: 'to find',
  almorzar: 'to have lunch', pedir: 'to ask for / order', servir: 'to serve', repetir: 'to repeat',
  jugar: 'to play', buscar: 'to look for', tocar: 'to touch / play (an instrument)', llegar: 'to arrive',
  pagar: 'to pay', salir: 'to go out', perder: 'to lose', sentir: 'to feel', seguir: 'to follow',
  costar: 'to cost', recordar: 'to remember', mostrar: 'to show', probar: 'to try',
};

export type Ending = 'ar' | 'er' | 'ir';
export const endingOf = (v: string): Ending | null =>
  (v.endsWith('ar') ? 'ar' : v.endsWith('er') ? 'er' : v.endsWith('ir') || v.endsWith('ír') ? 'ir' : null);

function stemChange(stem: string, change: 'e>ie' | 'o>ue' | 'e>i' | 'u>ue'): string {
  const [from, to] = change.split('>');
  const i = stem.lastIndexOf(from);
  return i < 0 ? stem : stem.slice(0, i) + to + stem.slice(i + 1);
}

/** Present tense. Stem changers change in every person but nosotros/vosotros. */
export function present(verb: string, p: Person): string | null {
  const e = endingOf(verb);
  if (!e) return null;
  let stem = verb.slice(0, -2);
  const change = STEM_CHANGERS[verb];
  if (change && p !== 'nosotros' && p !== 'vosotros') stem = stemChange(stem, change);
  // seguir → sigo (gu before o drops the u)
  if (p === 'yo' && stem.endsWith('gu') && e === 'ir') stem = stem.slice(0, -1);
  return stem + PRESENT[e][PEOPLE.indexOf(p)];
}

/** Regular preterite, with the yo spelling changes (-car → qué, -gar → gué, -zar → cé). */
export function preterite(verb: string, p: Person): string | null {
  const e = endingOf(verb);
  if (!e) return null;
  let stem = verb.slice(0, -2);
  if (p === 'yo' && e === 'ar') {
    if (stem.endsWith('c')) stem = stem.slice(0, -1) + 'qu';
    else if (stem.endsWith('g')) stem = stem.slice(0, -1) + 'gu';
    else if (stem.endsWith('z')) stem = stem.slice(0, -1) + 'c';
  }
  return stem + PRETERITE[e][PEOPLE.indexOf(p)];
}

/** NOT regular in the preterite — refused by the "regular preterite" topic. */
export const PRETERITE_IRREGULAR = new Set([
  'ser', 'ir', 'estar', 'tener', 'hacer', 'poder', 'poner', 'saber', 'querer', 'venir', 'decir', 'traer',
  'conducir', 'traducir', 'producir', 'dar', 'ver', 'andar', 'haber', 'caber', 'leer', 'creer', 'oír', 'caer',
  'construir', 'destruir', 'incluir', 'huir', 'reír', 'dormir', 'morir', 'pedir', 'servir', 'seguir', 'sentir',
  'preferir', 'mentir', 'repetir', 'vestir', 'medir',
]);

/** The present WITHOUT its stem change ("penso") — the classic mistake, used as a distractor. */
export function presentUnchanged(verb: string, p: Person): string | null {
  const e = endingOf(verb);
  return e ? verb.slice(0, -2) + PRESENT[e][PEOPLE.indexOf(p)] : null;
}
